/**
 * F3 -- one catalogue for the workflow states and job roles that reach
 * a business screen.
 *
 * My Work draws rows from five entity types, each with its own state
 * vocabulary, and rendered every one of them raw: an assessor saw
 * "sent_back" and a CRO saw "business_owner". Those are our column
 * values, not English.
 *
 * The union below is deliberately explicit rather than a
 * snake_case-to-Title-Case transform. "In review" and "Sent back"
 * happen to survive a mechanical transform; "no_evidence" becomes "No
 * evidence" but "cro" becomes "Cro", and a transform cannot know that
 * "in_signoff" is read "Awaiting sign-off". Anything unrecognised falls
 * back to the de-underscored form, so a state we have not catalogued
 * degrades to something readable rather than disappearing.
 */

import { i18n } from "shared/i18n";

const STATE_KEYS: readonly string[] = [
  "draft",
  "in_review",
  "in_signoff",
  "approved",
  "sent_back",
  "submitted",
  "rejected",
  "open",
  "closed",
  "challenged",
  "cro_signed",
  "business_owner_signed",
  "ops_res_signed",
  "service_owner_signed",
  "compliant",
  // Round 11 E1 -- the half of the enum pass that was missed.
  //
  // Five values reach the assessment grid and four of them were
  // catalogued. `partial` was not, so `statusLabel` fell through to
  // `humanise` and rendered a hard-coded "Partial" that
  // `markUntranslated` cannot see and a translator cannot reach at any
  // price -- the exact defect round 9 G moved these words into the
  // catalogue to remove, surviving in the one value nobody listed.
  // It is also the commonest judgement on the grid, so it was the
  // English word an Arabic reader saw most.
  "partial",
  // The same shape on the badge beside it: "tested" was an ad-hoc
  // string on the grid rather than a state word, so the two labels on
  // one row came from two places and only one of them was a
  // vocabulary. It is a state the product owns; it belongs here.
  "tested",
  "non_compliant",
  "no_evidence",
  "not_applicable",
  "candidate",
  "admitted",
  "included",
  "excluded",
  "removed",
  // Round 15 I7 -- a declaration's last state, now sent as a key.
  "superseded",
  "deferred",
];

/**
 * Round 10 E1 -- vocabularies that were reaching the screen as column
 * values with their underscores taken out.
 *
 * "no posture", "in progress", "third party" are not translations of
 * anything; they are the enum with a cosmetic pass. Each of these is a
 * closed set the product owns, so each gets words.
 */
const POSTURE_VERDICT_KEYS: readonly string[] = [
  "ok",
  "unproven",
  "insufficient",
  "no_posture",
];

const DRILL_STATUS_KEYS: readonly string[] = [
  "planned",
  "in_progress",
  "passed",
  "failed",
  "cancelled",
];

const FINDING_CATEGORY_KEYS: readonly string[] = [
  "cyber",
  "technology",
  "third_party",
  "process",
  "people",
  "facility",
  "data",
  "other",
];

/** A continuity-posture verdict as a reader should see it. */
export function postureVerdictLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("postureVerdict", key, POSTURE_VERDICT_KEYS) ?? humanise(raw);
}

/** A drill's state as a reader should see it. */
export function drillStatusLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("drillStatus", key, DRILL_STATUS_KEYS) ?? humanise(raw);
}

/** What kind of issue a finding is, as a reader should see it. */
export function findingCategoryLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("findingCategory", key, FINDING_CATEGORY_KEYS) ?? humanise(raw);
}

const ROLE_KEYS: readonly string[] = [
  "preparer",
  "reviewer",
  "approver",
  "cro",
  "business_owner",
  "service_owner",
  // P429 -- the second seat on a Critical Internal Function (Route 2).
  "cio",
  "head_of_opsres",
  // The SAME seat under the API's spelling. The sign-off endpoints
  // take `ops_res` and the realm calls it `head_of_opsres`
  // (SIGNOFF_REALM_ROLE in dossier/readiness_router.py owns that
  // distinction), and the backend puts the API word on the wire as
  // `next_role`. Without this key the lookup missed and the fallback
  // humanised the slug, so the readiness ceremony announced "now with
  // the Ops res" while the record three clicks away named the seat
  // properly. Round 8 found it, round 9 recorded it fixed, round 10
  // found it back on the same ceremony.
  "ops_res",
  "ae",
  "examiner",
  "admin",
  "submitter",
  "challenge",
];

/**
 * Round 9 G -- the words live in the catalogue now.
 *
 * These maps were hard-coded English in this file, which
 * `markUntranslated` never sees -- so they could not translate at any
 * price, and under the pseudo-locale they rendered as bare English.
 *
 * What stays here is the closed VOCABULARY: which values we have a
 * word for. What moved is the words. The helpers below keep their
 * signatures, because roughly sixty call sites depend on them, and
 * keep their fallback: an uncatalogued value still degrades to a
 * readable de-underscored form rather than disappearing.
 *
 * `i18n.t` rather than the `useTranslation` hook, because these are
 * plain functions called from render, from sort comparators and from
 * tests. Every call site is already inside a component that
 * subscribes to the language, so a switch re-renders them.
 */
function fromCatalogue(
  group: string,
  key: string,
  known: readonly string[],
): string | null {
  if (!known.includes(key)) return null;
  const out = i18n.t(`vocab.${group}.${key}`);
  // A key the catalogue has not got back returns the key itself, which
  // would put "vocab.state.draft" on a screen. Treat that as unknown
  // and let the caller fall back to the readable form.
  return out.startsWith("vocab.") ? null : out;
}

function humanise(raw: string): string {
  const spaced = raw.replace(/_/g, " ").trim();
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A workflow state as a reader should see it. */
export function stateLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const known = fromCatalogue("state", key, STATE_KEYS);
  if (known) return known;
  // My Work also emits composite states like "awaiting cro" for the
  // governance ceremonies. Translate the role half rather than losing
  // the whole line to the fallback.
  const awaiting = /^awaiting[ _]+(.+)$/.exec(key);
  if (awaiting && awaiting[1]) {
    return i18n.t("vocab.awaitingRole", { role: roleLabel(awaiting[1]) });
  }
  return humanise(raw);
}

/**
 * How a letter reached the bank, as a reader should see it.
 *
 * F -- `pdf_upload` and `pasted` are our storage words, and they were
 * printed raw beneath the letter's title. Worse, a circular the bank
 * pasted in itself read exactly like one a regulator sent: same page,
 * same heading, same "Create CAP" button. What a reader needs from this
 * line is whether the document came from outside.
 */
const LETTER_SOURCE_KEYS: readonly string[] = ["pdf_upload", "pasted", "internal"];

export function letterSourceLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("letterSource", key, LETTER_SOURCE_KEYS) ?? humanise(raw);
}

/** How a service is classified, as a reader should see it. */
/*
 * Round 9 E8 -- one set of words, and a duplicate of my own making.
 *
 * C1 earlier this round created shared/classification/labels.ts with a
 * SECOND map of these same four values -- "IBS supporting",
 * "Non-critical" -- whose docstring says "a second copy is how the two
 * drift" while being the second copy. It is folded in here.
 *
 * Two forms, because there are genuinely two jobs and collapsing them
 * is what produced the awkward renderings on both sides: a BADGE wants
 * a short noun, a SENTENCE wants a fragment that reads. The E7 kind
 * vocabulary has the same shape for the same reason.
 */
export type ServiceClassification =
  "ibs_supporting" | "supporting" | "non_critical" | "out_of_scope";

/** Short form, for a badge or a narrow column. */
export /** The four classifications, in register order. The WORDS come from
 *  `classificationLabel`; this is which values exist. */
const CLASSIFICATION_KEYS: readonly string[] = [
  "ibs_supporting",
  "supporting",
  "non_critical",
  "out_of_scope",
];

/** Mid-sentence form, for prose explaining the decision. */
const CLASSIFICATION_SENTENCE_KEYS: readonly string[] = [
  "ibs_supporting",
  "supporting",
  "non_critical",
  "out_of_scope",
];

export function classificationInSentence(raw: string | null | undefined): string {
  if (!raw) return "unclassified";
  const key = raw.trim().toLowerCase();
  return (
    fromCatalogue("classificationInSentence", key, CLASSIFICATION_SENTENCE_KEYS) ??
    humanise(raw)
  );
}

/**
 * Round 9 E8 -- the four outcome categories, as a reader sees them.
 *
 * These existed as OUTCOME_LABEL inside foundation/admin/ibsAdapter,
 * where the admin register uses them. `twin` cannot import `foundation`
 * -- the module boundary is deliberate -- so the IBS admission
 * register, which is the screen a business reader actually meets,
 * printed `minimize_customer_harm` under every IBS name for four
 * rounds. The label was written; it was just on the wrong side of a
 * wall.
 *
 * Here, beside the classification vocabulary, because both answer the
 * same question: what does this column value mean to somebody who does
 * not read our schema.
 */
const OUTCOME_CATEGORY_KEYS: readonly string[] = [
  "minimize_customer_harm",
  "retain_customer_confidence",
  "regulatory_engagement",
  "protect_safety_and_soundness",
];

export function outcomeCategoryLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("outcomeCategory", key, OUTCOME_CATEGORY_KEYS) ?? humanise(raw);
}

export function classificationLabel(raw: string | null | undefined): string {
  if (!raw) return "unclassified";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("classification", key, CLASSIFICATION_KEYS) ?? humanise(raw);
}

/** An assessment judgement as a reader should see it. */
export function statusLabel(raw: string | null | undefined): string {
  return stateLabel(raw);
}

/** A position or job role as a reader should see it. */
export function roleLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return fromCatalogue("role", key, ROLE_KEYS) ?? humanise(raw);
}

/** The short form used inside a badge, where space is tight. */
export function roleBadge(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  // The long ceremonial names do not fit a badge; the acronym does,
  // and is what the person holding the role calls themselves.
  return fromCatalogue("roleBadge", key, ROLE_BADGE_KEYS) ?? roleLabel(raw);
}

const ROLE_BADGE_KEYS: readonly string[] = ["cro", "head_of_opsres", "ae", "challenge"];

/**
 * Round 11 F3 -- the qualifiers the six-layer block prints after the
 * kind: what a service is classified as, how a supplier edge was
 * derived, what a body of data is and what a service does with it.
 *
 * ONE flat set, which is safe here and is not safe in general: the
 * five vocabularies folded in below are disjoint, and
 * `layer_detail_vocabularies_do_not_collide` in the frontend suite
 * asserts it stays that way. The governance catalogue is keyed by LINE
 * precisely because ITS vocabularies do collide (`out_of_scope` is
 * both a classification and an MVB tier); these do not, and keying
 * them by line would mean the server telling the client which of five
 * a value came from when the value already says.
 */
export const LAYER_DETAIL_KEYS: readonly string[] = [
  // Service classification (the four the register uses).
  "ibs_supporting",
  "supporting",
  "non_critical",
  "out_of_scope",
  // How a service <-> supplier edge got here.
  "cmdb_derived",
  "architecture_connector",
  // Round 20 V6 -- a material supplier with no exit plan.
  "no_exit_plan",
  // Round 20 V9 -- a recovery team with no one in it.
  "no_members",
  // H -- the CMDB classes this estate actually carries, without the
  // vendor's table prefix. Anything else falls through to a stripped,
  // humanised, lower-cased form rather than being dropped.
  "linux_server",
  "db_instance",
  "atm",
  "lb_appliance",
  "app_server",
  "network_gear",
  "storage",
  "vm",
  "container",
  "cluster",
  // What a body of data is.
  "public",
  "internal",
  "confidential",
  "secret",
  "customer_personal",
  "payment",
  "account",
  "transaction",
  "employee",
  "operational",
  "regulatory",
  // ... and what the service does with it.
  "stores",
  "processes",
  "transmits",
  // P429.2 -- funding resources and regulatory commitments.
  "settlement_account",
  "facility",
  "buffer",
  "market_access",
  "counterparty",
  "return",
  "notification",
  "limit",
  "licence_condition",
  "bound_by",
  "generates",
];

/**
 * Round 11 F3 -- how a service keeps running when something fails.
 *
 * These words existed as a hard-coded English map inside
 * `ContinuityPostureView`, which `markUntranslated` never sees -- and a
 * SECOND copy was about to be needed on the disposition page, where the
 * value was being printed raw ("continuity posture active_passive").
 * One catalogue, two readers.
 */
const CONTINUITY_POSTURE_KEYS: readonly string[] = [
  "active_active",
  "active_passive",
  "backup_restore",
  "manual_workaround",
  "none",
];

/** A service's continuity posture as a reader should see it. */
export function continuityPostureLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  return (
    fromCatalogue("continuityPosture", key, CONTINUITY_POSTURE_KEYS) ?? humanise(raw)
  );
}

/** A dependency-layer qualifier as a reader should see it. */
/**
 * ROUND 13 H -- a ServiceNow CI class is the VENDOR's table name.
 *
 * Six detail rows ended in `cmdb_ci_linux_server`,
 * `cmdb_ci_db_instance`, `cmdb_ci_lb_appliance` and the like. The
 * component rendered them verbatim on the reasoning that `detail`
 * carries "the bank's own words -- a CMDB class, a country list". A
 * country list is the bank's words. A class token is an identifier in
 * somebody else's schema, and it read as leaked plumbing in a row of
 * plain phrases.
 *
 * The prefix is stripped and the remainder humanised, so a bank's own
 * custom class still renders as something a person can read rather
 * than being dropped or guessed at.
 */
function stripCmdbPrefix(key: string): string {
  return key.startsWith("cmdb_ci_") ? key.slice("cmdb_ci_".length) : key;
}

export function layerDetailLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const known =
    fromCatalogue("layerDetail", key, LAYER_DETAIL_KEYS) ??
    fromCatalogue("layerDetail", stripCmdbPrefix(key), LAYER_DETAIL_KEYS);
  if (known) return known;
  /* H -- and lower-cased, because these sit in a dot-separated run of
     lowercase phrases ("SARIE Gateway · service · supporting"). A
     title-cased fall-through put "Container" in the middle of one. */
  return humanise(stripCmdbPrefix(key)).toLowerCase();
}

export const CATALOGUED_STATES = [...STATE_KEYS];
export const CATALOGUED_ROLES = [...ROLE_KEYS];

/**
 * Framework and jurisdiction codes carry acronyms, which a mechanical
 * title-case gets wrong: `cbuae_outsourcing` became "Cbuae
 * Outsourcing" on the obligations panel. The regulator's own name is
 * not a word to be capitalised; it is an acronym.
 *
 * Only the acronyms are catalogued. Everything else -- including a
 * custom framework a bank imports -- falls through the same
 * title-casing as before, so this narrows a defect without claiming
 * knowledge of names we do not have.
 */
const ACRONYMS = new Set([
  "sama",
  "cbuae",
  "dora",
  "eu",
  "itgf",
  "csf",
  "bcm",
  "crfr",
  "ksa",
  "uae",
  "it",
]);

export function frameworkLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}
